/**
 * Branch Details Modal
 * Shows complete branch information
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import branchService from '../../services/branches';
import './BranchDetailsModal.css';

const BranchDetailsModal = ({ branchId, onClose, onUpdate }) => {
  const navigate = useNavigate();
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchBranchDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const branchData = await branchService.getBranchById(branchId);
      setBranch(branchData);
    } catch (err) {
      setError(err.message || 'Failed to load branch details');
      console.error('Branch details error:', err);
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    if (branchId) {
      fetchBranchDetails();
    }
  }, [branchId, fetchBranchDetails]);

  const getTypeColor = (type) => {
    const colors = {
      'Main': '#3498DB',
      'Branch': '#F39C12',
      'Hub': '#9B59B6',
      'Warehouse': '#1ABC9C'
    };
    return colors[type] || '#666';
  };

  const handleEditBranch = () => {
    navigate(`/edit-branch/${branchId}`);
    onClose();
  };

  const handleViewOnMap = () => {
    if (branch?.latitude && branch?.longitude) {
      window.open(
        `https://www.google.com/maps?q=${branch.latitude},${branch.longitude}`,
        '_blank'
      );
    }
  };

  if (!branchId) return null;

  return (
    <div className="branch-details-overlay" onClick={onClose}>
      <div className="branch-details-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="branch-details-close" onClick={onClose}>
          &times;
        </button>

        {loading ? (
          <div className="branch-details-loading">
            <div className="spinner"></div>
            <p>Loading branch details...</p>
          </div>
        ) : error ? (
          <div className="branch-details-error">
            <p>{error}</p>
            <button onClick={onClose} className="btn-secondary">Close</button>
          </div>
        ) : branch ? (
          <div className="branch-details-content">
            {/* Header */}
            <div className="branch-details-header">
              <div className="branch-details-header-left">
                <span className="branch-details-icon">📍</span>
                <div>
                  <h2 className="branch-details-title">{branch.name}</h2>
                  <div className="branch-details-badges">
                    <span
                      className="branch-details-type-badge"
                      style={{ backgroundColor: getTypeColor(branch.type) }}
                    >
                      {branch.type}
                    </span>
                    <span
                      className={`branch-details-status-badge ${branch.isActive ? 'active' : 'inactive'}`}
                    >
                      {branch.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="branch-details-code">
                Code: <strong>{branch.code}</strong>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="branch-details-actions">
              <button
                onClick={handleEditBranch}
                className="branch-details-btn branch-details-btn-edit"
              >
                Edit Branch
              </button>
              {branch.latitude && branch.longitude && (
                <button
                  onClick={handleViewOnMap}
                  className="branch-details-btn branch-details-btn-secondary"
                >
                  View on Map
                </button>
              )}
            </div>

            {/* Main Content Grid */}
            <div className="branch-details-grid">
              {/* Left Column */}
              <div className="branch-details-left">
                {/* Address Section */}
                <div className="branch-details-section">
                  <h3 className="branch-details-section-title">Address</h3>
                  <div className="branch-details-address">
                    <p className="branch-details-address-line">{branch.address?.street}</p>
                    <p className="branch-details-address-line">
                      {branch.address?.city}, {branch.address?.state} {branch.address?.zipCode}
                    </p>
                    <p className="branch-details-address-line">{branch.address?.country}</p>
                  </div>
                  {branch.fullAddress && (
                    <p className="branch-details-full-address">{branch.fullAddress}</p>
                  )}
                </div>

                {/* Contact Section */}
                <div className="branch-details-section">
                  <h3 className="branch-details-section-title">Contact Information</h3>
                  <div className="branch-details-contact-grid">
                    <div className="branch-details-contact-item">
                      <span className="branch-details-contact-icon">📞</span>
                      <div>
                        <p className="branch-details-contact-label">Phone</p>
                        <p className="branch-details-contact-value">{branch.contact?.phone || 'N/A'}</p>
                      </div>
                    </div>
                    {branch.contact?.email && (
                      <div className="branch-details-contact-item">
                        <span className="branch-details-contact-icon">📧</span>
                        <div>
                          <p className="branch-details-contact-label">Email</p>
                          <p className="branch-details-contact-value">{branch.contact.email}</p>
                        </div>
                      </div>
                    )}
                    {branch.contact?.manager && (
                      <div className="branch-details-contact-item">
                        <span className="branch-details-contact-icon">👤</span>
                        <div>
                          <p className="branch-details-contact-label">Manager</p>
                          <p className="branch-details-contact-value">{branch.contact.manager}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Notes Section */}
                {branch.notes && (
                  <div className="branch-details-section">
                    <h3 className="branch-details-section-title">Notes</h3>
                    <p className="branch-details-notes">{branch.notes}</p>
                  </div>
                )}
              </div>

              {/* Right Column */}
              <div className="branch-details-right">
                {/* Coordinates Section */}
                <div className="branch-details-section">
                  <h3 className="branch-details-section-title">Location Coordinates</h3>
                  <div className="branch-details-coords">
                    <div className="branch-details-coord-item">
                      <span className="branch-details-coord-label">Latitude</span>
                      <span className="branch-details-coord-value">
                        {branch.latitude?.toFixed(6) || 'N/A'}
                      </span>
                    </div>
                    <div className="branch-details-coord-item">
                      <span className="branch-details-coord-label">Longitude</span>
                      <span className="branch-details-coord-value">
                        {branch.longitude?.toFixed(6) || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default BranchDetailsModal;
